import { createFileRoute, redirect } from "@tanstack/react-router";
import { AuthStatus } from "@/common/enums/enums";
import {
  clearPendingLaunch,
  type LaunchParams,
  launchSearch,
  parseLaunchParams,
  rememberPendingLaunch,
} from "@/lib/emr-launch";
import { LaunchPage } from "@/pages/Launch";
import { useAuthStore } from "@/store/auth.store";

/**
 * EMR launch link. Sanitizes the query (see lib/emr-launch.ts), then:
 * - signed out: remembers the launch in this tab and goes to sign-in, which
 *   continues here afterwards;
 * - new-visit: Visits with the New visit sheet open;
 * - latest-visit: resolved by LaunchPage once the visit list loads.
 */
export const launchBeforeLoad = ({
  search,
  location,
}: {
  search: LaunchParams;
  location?: { search: Record<string, unknown> };
}): void => {
  const { authStatus } = useAuthStore.getState();
  if (authStatus !== AuthStatus.AUTHENTICATED) {
    rememberPendingLaunch(search);
    throw redirect({ to: "/" });
  }
  clearPendingLaunch();
  if (search.action === "new-visit")
    throw redirect({
      to: "/visits",
      search: {
        openNewVisit: true,
        ...(search.template ? { template: search.template } : {}),
      },
      replace: true,
    });
  // latest-visit: drop anything extra from the address bar before the page
  // loads (the redirect lands here again with only the clean parameters).
  const clean = launchSearch(search);
  const raw = location?.search ?? {};
  if (
    Object.keys(raw).length !== Object.keys(clean).length ||
    Object.entries(clean).some(([key, value]) => raw[key] !== value)
  )
    throw redirect({
      to: "/launch",
      search: {
        action: search.action,
        ...(search.template ? { template: search.template } : {}),
        ...(search.source ? { source: search.source } : {}),
      },
      replace: true,
    });
};

export const Route = createFileRoute("/launch")({
  validateSearch: (search: Record<string, unknown>): LaunchParams =>
    parseLaunchParams(search),
  beforeLoad: launchBeforeLoad,
  component: LaunchPage,
});
